import { effect, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { EnteAutocompleteItem } from 'cdc-conte-ui-kit';
import {
  extractTipoEnteIds,
  TipoEnteControlValue,
} from '../models/interfaces/documenti-adempimenti/filtri-ricerca-gestione-documenti.interface';
import {
  RicercaRichiesteNuovoInvioParams,
  toRicercaDateParam,
} from '../models/interfaces/documenti-adempimenti/filtri-ricerca-richieste-nuovo-invio.interface';
import { FiltriRichiesteNuovoInvioStore } from '../store/filtri-documenti-richieste-nuovo-invio';
import { FormGroup } from '@angular/forms';
import { SelectOptionsInterface } from '../models/interfaces/documenti-adempimenti/carica-documento.interfaces';
import { CachedUrlFiltriRicercaStore, RouterServiceConte } from './router-service';
import { DEFAULT_STATO_RICHIESTA } from '../features/documenti-adempimenti/richieste-nuovo-invio/filtri-ricerca-richieste-nuovo-invio/handlers-filtri/filtri-ricerca-richieste-nuovo-invio.form';

@Injectable({
  providedIn: 'root',
})
export class FiltriRicercaRichiesteNuovoInvioService {
  reloadDocuments$ = new Subject<void>();
  indietroBtnHandler$ = new BehaviorSubject<null | boolean>(null);
  private readonly store = inject(FiltriRichiesteNuovoInvioStore);
  routerServiceConte = inject(RouterServiceConte);
  defaultStatoRichiesta = signal<SelectOptionsInterface>(DEFAULT_STATO_RICHIESTA);


  extractSelectId(value: SelectOptionsInterface | number | null | undefined): number | undefined {
    if (value == null) {
      return undefined;
    }
    return typeof value === 'number' ? value : value.id;
  }

   buildRicercaParams(form: FormGroup, formFilters: FormGroup, adempimentoNormativoVal: boolean, idToAssignToFase: number, is_GAR_ASR_CFR_FEV: boolean, isTrentoOrBolzano:boolean): RicercaRichiesteNuovoInvioParams {
    const params: RicercaRichiesteNuovoInvioParams = {};

    const rawText = form.get('search')?.value;
    const text = typeof rawText === 'string' && rawText.length > 1 ? rawText.trim() : '';
    if (text) {
      params.text = text;
    }

    const esercizio = formFilters.get('esercizio')?.value;
    const sequEsercizio = this.extractSelectId(esercizio);
    if (sequEsercizio != null) {
      params.sequEsercizio = sequEsercizio;
    }

    const fase = formFilters.get('fase')?.value;
    const sequFase = adempimentoNormativoVal && idToAssignToFase
      ? idToAssignToFase
      : this.extractSelectId(fase);
    if (sequFase != null) {
      params.sequFase = sequFase;
    }

    const listaTipiEnte = extractTipoEnteIds(formFilters.get('tipoEnte')?.value as TipoEnteControlValue);
    if (listaTipiEnte.length) {
      params.listaTipiEnte = listaTipiEnte;
    }

    const sequRegione =  this.extractSelectId(formFilters.get('regione')?.value as SelectOptionsInterface);

    if(is_GAR_ASR_CFR_FEV){
      params.sequRegione = undefined
    } else if (sequRegione != null) {
       params.sequRegione = sequRegione
    }

    const sequProvincia = this.extractSelectId(formFilters.get('provinciaCittaMetropolitana')?.value as SelectOptionsInterface);
    if(is_GAR_ASR_CFR_FEV && isTrentoOrBolzano){
      params.sequRegione = undefined
    } else if (sequProvincia != null) {
      params.sequProvincia = sequProvincia;
    }

    const denominazioneEnte = formFilters.get('denominazioneEnte')?.value;
    if (denominazioneEnte && typeof denominazioneEnte === 'object' && denominazioneEnte.idEnte != null) {
      params.sequEnte = String(denominazioneEnte.idEnte);
      // Salviamo anche l'oggetto completo (con la descrizione), non ricostruibile dal solo id,
      // per poter ripristinare visivamente il campo 'Denominazione ente' al ritorno sulla pagina.
      this.store.saveDenominazioneEnteSelezionata(denominazioneEnte as EnteAutocompleteItem);
    } else {
      this.store.saveDenominazioneEnteSelezionata(null);
    }

    const sequAdempimento = this.extractSelectId(formFilters.get('adempimentoNormativo')?.value);
    if (sequAdempimento != null) {
      params.sequAdempimento = sequAdempimento;
    }

    const sequTipoDocumento = this.extractSelectId(formFilters.get('tipoDocumento')?.value);
    if (sequTipoDocumento != null) {
      params.sequTipoDocumento = sequTipoDocumento;
    }

    const sequStato = this.extractSelectId(formFilters.get('statoRichiesta')?.value);
    if (sequStato != null) {
      params.sequStato = sequStato;
    } else {
      // Se l'utente non ha selezionato alcuno stato, impostiamo il default (in modo da non mostrare tutte le richieste, ma solo quelle in stato "Inviata").
      params.sequStato = null;
    }

    const dataRichiestaMin = formFilters.get('dataRichiestaMin')?.value;
    if (dataRichiestaMin) {
      params.dataRichiestaMin = toRicercaDateParam(dataRichiestaMin);
    }

    const dataRichiestaMax = formFilters.get('dataRichiestaMax')?.value;
    if (dataRichiestaMax) {
      params.dataRichiestaMax = toRicercaDateParam(dataRichiestaMax);
    }

    this.buildStoreFilter(params);

    return params;
  }

  /**
   * Ripristina nei form i valori precedentemente salvati nello store (operazione inversa di
   * `buildRicercaParams`), così da mostrare all'utente gli stessi filtri usati nell'ultima ricerca
   * (es. al ritorno dalla pagina di dettaglio documento tramite il tasto 'Indietro').
   * @important la denominazione ente NON viene ripristinata da questo metodo: lo store conserva qui
   * solo l'id (sequEnte), non l'oggetto completo (con relativa descrizione) mostrato nell'autocomplete.
   * Il ripristino visivo di quel campo viene gestito separatamente dal chiamante, a partire
   * dall'oggetto completo salvato in `denominazioneEnteSelezionata` nello store.
   */
  applyParamsToForm(form: FormGroup, formFilters: FormGroup, params: RicercaRichiesteNuovoInvioParams): void {
    form.get('search')?.setValue(params.text ?? '', { emitEvent: false });

    const patchIfPresent = (controlName: string, value: unknown): void => {
      if (value === null || value === undefined) {
        return;
      }
      formFilters.get(controlName)?.setValue(value, { emitEvent: false });
    };

    patchIfPresent('esercizio', params.sequEsercizio);
    patchIfPresent('fase', params.sequFase);
    // Lo store conserva i soli id dei tipi ente: il multiselect li accetta e risolve da sé
    // l'etichetta dal proprio dataset. La lettura è convertita da `extractTipoEnteIds`, che
    // gestisce sia gli id grezzi ripristinati qui sia gli oggetti emessi dal widget.
    if (params.listaTipiEnte?.length) {
      patchIfPresent('tipoEnte', [...params.listaTipiEnte]);
    }
    patchIfPresent('regione', params.sequRegione);
    patchIfPresent('provinciaCittaMetropolitana', params.sequProvincia);
    patchIfPresent('adempimentoNormativo', params.sequAdempimento);
    patchIfPresent('tipoDocumento', params.sequTipoDocumento);
    // A differenza degli altri campi, 'statoRichiesta' va ripristinato anche quando è null:
    // un valore null in `params` riflette la scelta esplicita dell'utente di rimuoverlo
    // dall'ultima ricerca, e non va sovrascritto dal default impostato alla creazione del form.
    formFilters.get('statoRichiesta')?.setValue(params.sequStato ?? null, { emitEvent: false });
    patchIfPresent('dataRichiestaMin', params.dataRichiestaMin);
    patchIfPresent('dataRichiestaMax', params.dataRichiestaMax);
  }

  hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') return !isNaN(value);
  return true;
}

 hasAtLeastOneFilter(filtriStoreObj: RicercaRichiesteNuovoInvioParams): boolean {
  return Object.values(filtriStoreObj).some(this.hasValue);
}

  buildStoreFilter(oggettoFiltri: RicercaRichiesteNuovoInvioParams){
    const filtriStoreObj: RicercaRichiesteNuovoInvioParams = {
      text: oggettoFiltri?.text,
      sequEsercizio: oggettoFiltri?.sequEsercizio,
      sequFase: oggettoFiltri?.sequFase,
      listaTipiEnte: oggettoFiltri?.listaTipiEnte,
      sequRegione: oggettoFiltri?.sequRegione,
      sequProvincia: oggettoFiltri?.sequProvincia,
      sequEnte: oggettoFiltri?.sequEnte,
      sequAdempimento: oggettoFiltri?.sequAdempimento,
      sequTipoDocumento: oggettoFiltri?.sequTipoDocumento,
      sequStato: oggettoFiltri?.sequStato,
      dataRichiestaMin: oggettoFiltri?.dataRichiestaMin,
      dataRichiestaMax: oggettoFiltri?.dataRichiestaMax,
    };

    // sequStato === null significa che l'utente ha rimosso esplicitamente il filtro 'Stato Richiesta'
    // (il form lo valorizza sempre al default 'Non evasa', quindi null è sempre un'azione intenzionale):
    // va preservato nello store anche quando non ci sono altri filtri attivi, altrimenti la ricerca
    // verrebbe considerata "vuota" e lo store azzerato, perdendo la scelta dell'utente.
    const hasAnyValue = this.hasAtLeastOneFilter(filtriStoreObj) || filtriStoreObj.sequStato === null;

    if (hasAnyValue) {
      this.store.saveFiltriRicerca(filtriStoreObj);
    } else {
      this.store.resetState();
    }
  }

  /**
   *  @desc metodo per resettare i filtri di ricerca leggendo la route (uscendo da url 'richieste-nuovo-invio', lo store dei filtri di ricerca viene resettato )
   */
  handlerFiltriCachati() {
    effect(() => {
      const resettaFiltri = this.routerServiceConte.resetFiltriCachati(
        CachedUrlFiltriRicercaStore.RICHIESTE_NUOVO_INVIO,
      );
      if (resettaFiltri) {
        this.store.resetState();
        this.reloadDocuments$.next();
      }
    });

  }

  /**
   * Precompila i filtri della tab con i valori del caricamento documento.
   * `sequStato` arriva dalla modale (Non evasa o In evasione). Se assente resta
   * il default della tab. Il salvataggio passa da `buildStoreFilter`, così cache
   * e tasto Indietro rileggono lo stesso store.
   */
  precompilaDaCaricamentoDocumento(
    params: Pick<
      RicercaRichiesteNuovoInvioParams,
      'sequAdempimento' | 'sequFase' | 'sequTipoDocumento' | 'sequEsercizio' | 'sequStato'
    >,
  ): void {
    this.buildStoreFilter({
      sequAdempimento: params.sequAdempimento,
      sequFase: params.sequFase,
      sequTipoDocumento: params.sequTipoDocumento,
      sequEsercizio: params.sequEsercizio,
      sequStato: params.sequStato ?? this.defaultStatoRichiesta().id,
    });
  }

}
